/**
 * Scrubbing a chart with a mouse or a finger.
 *
 * The naive version listens for `pointermove` and `pointerleave` only, which is
 * fine for a mouse and poor on touch:
 *
 *   - the browser claims the gesture for scrolling and cancels the stream, so
 *     tracking dies partway through a drag;
 *   - once the finger passes outside the element the events stop arriving;
 *   - `pointerleave` then fires and clears the readout mid-gesture.
 *
 * The fixes are `touch-action: pan-y` — vertical scrolling still belongs to the
 * page, horizontal dragging belongs to the chart — plus pointer capture so the
 * finger keeps its grip after leaving the element, and treating "leave" as an
 * end only for a mouse.
 */
export interface PointerTracking {
  /**
   * Called with the client position of the pointer, for both hover and drag.
   *
   * `y` carries because a stacked chart has to know which panel is being
   * pointed at — the readout has to keep off that one.
   */
  onMove: (clientX: number, clientY: number) => void;
  /** Called when the interaction is over and the readout should clear. */
  onEnd: () => void;
}

export function trackChartPointer(el: SVGElement, { onMove, onEnd }: PointerTracking): void {
  // Horizontal drags scrub; vertical ones still scroll the page.
  el.style.touchAction = 'pan-y';

  el.addEventListener('pointerdown', (e) => {
    // Keeps events coming even once the finger strays outside the chart.
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* Capture is a nicety; scrubbing still works without it. */
    }
    onMove(e.clientX, e.clientY);
  });

  el.addEventListener('pointermove', (e) => onMove(e.clientX, e.clientY));

  el.addEventListener('pointerup', (e) => {
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* Already released. */
    }
    // A mouse is still hovering after the button comes up; a finger is gone.
    if (e.pointerType !== 'mouse') onEnd();
  });

  el.addEventListener('pointercancel', onEnd);

  // Only a mouse "leaves" without ending the gesture; a captured touch reports
  // leave constantly mid-drag and must not clear the readout.
  el.addEventListener('pointerleave', (e) => {
    if (e.pointerType === 'mouse') onEnd();
  });
}
